import { isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Composition,
  CompositionSection,
  Encounter,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { isEmpty } from 'lodash-es'
import {
  codeFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import { SEEN_HEALTH_SYSTEM } from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

export class ExtendedComposition extends ExtendedResource<Composition> {
  static readonly RESOURCE_TYPE = 'Composition'

  static readonly DESK_CREATED_ON_TAG_SYSTEM = `${SEEN_HEALTH_SYSTEM}/Composition/deskCreatedOn`

  static isInstance(value: unknown): value is Composition {
    return isResource(value) && isOfType(value, 'Composition')
  }

  /* The composition a resource is filed under, found from the resource itself.
   * The link runs one way — a composition lists the resources its sections
   * embed in `section.entry`, and nothing is written back onto the embedded
   * resource — so the parent can only be reached by searching for the
   * composition that names it. `entry` is the standard
   * `Composition.section.entry` search parameter.
   *
   * Deliberately not filtered by category or type. Every Orbit-native
   * composition a clinician can write into — an assessment, a visit note, any
   * kind yet to exist — can embed a validated tool through the editor, and a
   * category filter here would let a tool embedded in the kind it omits read
   * as having no parent at all.
   *
   * Comma-separated references are an OR, so a caller holding several embedded
   * resources resolves all their parents in one search. */
  static byEntry(entryReferences: string[]): QueryTypes {
    return {
      'status:not': 'entered-in-error',
      entry: entryReferences.join(','),
    }
  }

  get status(): Composition['status'] {
    return this.resource.status
  }

  set status(status: Composition['status']) {
    this.resource.status = status
  }

  get typeCode(): string | undefined {
    return codeFromCodeableConcept(this.resource.type)
  }

  get title(): string | undefined {
    return this.resource.title
  }

  get dateString(): string | undefined {
    return this.resource.date
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  set encounter(encounter: Reference<Encounter> | undefined) {
    this.resource.encounter = encounter
  }

  addAttesterPractitioner(practitioner: Reference<Practitioner>) {
    this.resource.attester ??= []
    this.resource.attester.push({
      party: practitioner,
      mode: 'professional',
      time: now(),
    })
  }

  get attester(): Composition['attester'] {
    return this.resource.attester
  }

  set attester(attester: Composition['attester']) {
    this.resource.attester = isEmpty(attester) ? undefined : attester
  }

  get event(): Composition['event'] {
    return this.resource.event
  }

  set event(event: Composition['event']) {
    this.resource.event = isEmpty(event) ? undefined : event
  }

  get authorIds(): string[] {
    return this.resource.author?.map((a) => resolveId(a) ?? '')
  }

  get sectionsSummary():
    | {
        title?: string
        code?: string
        author?: string[]
        text?: string
      }[]
    | undefined {
    return this.resource.section?.map((s) => ({
      title: s.title,
      code: codeFromCodeableConcept(s.code),
      author: s.author?.map((id) => resolveId(id) ?? ''),
      text: s.text?.div,
    }))
  }

  get categoryCodes(): string[] | undefined {
    return this.resource.category?.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get subject(): Reference | undefined {
    return this.resource.subject
  }

  get patientSubject(): Reference<Patient> | undefined {
    const subject = this.subject
    return subject && isOfType(subject, 'Patient') ? subject : undefined
  }

  // Exposed on the base so activity-feed diffing works for any Composition.
  // `Assessment` overrides `sections` with a paired setter.
  get sections(): CompositionSection[] {
    return this.resource.section ?? []
  }

  get signedOn(): string | undefined {
    return this.resource.attester?.[0]?.time
  }

  sectionByCode(code: CodeableConcept): CompositionSection | undefined {
    return this.resource.section?.find((s) => hasCodeableConcept(s.code, code))
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      type: this.typeCode,
      category: this.categoryCodes,
      title: this.title,
      date: this.dateString,
      authorIds: this.authorIds,
      patientId: resolveId(this.subject),
      sections: this.sectionsSummary,
    }
  }
}
